import { SaveOutlined, UserOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tabs,
} from "antd";
import { Api, hasPermission, IJourney, IUser } from "@model";
import { StickyFormActions } from "@components";
import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { UserLogTimeline } from "../Dashboard/UserLogTimeline";
import {
  ChatTranscript,
  JourneyMessage,
  JourneyProgressSteps,
  JourneyStepProgress,
  LessonAttempt,
} from "../Journey/JourneyLogsModal";

const formatDate = (value?: string | Date | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatBool = (value?: boolean) => (value ? "Yes" : "No");

interface UserJourneyLogSummary {
  journey: Pick<IJourney, "_id" | "title" | "steps" | "metadata">;
  userJourney: {
    _id: string;
    status: string;
    currentStepIndex: number;
    startedAt: string;
    completedAt?: string;
    stepProgress: JourneyStepProgress[];
    chatTranscripts?: ChatTranscript[];
  };
}

interface UserJourneyLogDetails extends UserJourneyLogSummary {
  lessonAttempts: LessonAttempt[];
  messages: JourneyMessage[];
}

const journeyTitle = (journey: UserJourneyLogSummary["journey"]) =>
  journey.title ||
  journey.metadata?.en?.title ||
  Object.values(journey.metadata ?? {}).find((entry) => entry?.title)?.title ||
  journey._id;

const journeyProgressLabel = (
  userJourney: UserJourneyLogSummary["userJourney"],
) => {
  const totalSteps = userJourney.stepProgress.length;
  if (!totalSteps) return "—";
  return `${Math.min(userJourney.currentStepIndex + 1, totalSteps)} / ${totalSteps}`;
};

const journeyStatusTag = (status: string) => (
  <Tag
    color={
      status === "completed"
        ? "green"
        : status === "in_progress"
          ? "blue"
          : "default"
    }
  >
    {status.replace("_", " ")}
  </Tag>
);

const UserJourneyProgressTab = ({ userId }: { userId: string }) => {
  const [logs, setLogs] = useState<UserJourneyLogSummary[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [selectedLog, setSelectedLog] = useState<UserJourneyLogSummary>();
  const [details, setDetails] = useState<UserJourneyLogDetails>();
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    let active = true;
    setSelectedLog(undefined);
    setDetails(undefined);
    setLoadingLogs(true);

    Api.get(`/api/admin/panel/journeys/user-logs/${userId}`)
      .then((response) => {
        if (active) setLogs(response.data?.data ?? []);
      })
      .catch(() => {
        if (active) message.error("Failed to load journey progress");
      })
      .finally(() => {
        if (active) setLoadingLogs(false);
      });

    return () => {
      active = false;
    };
  }, [userId]);

  const openJourneyLog = async (log: UserJourneyLogSummary) => {
    setSelectedLog(log);
    setDetails(undefined);
    setLoadingDetails(true);
    try {
      const response = await Api.get(
        `/api/admin/panel/journeys/${log.journey._id}/logs/${log.userJourney._id}`,
      );
      setDetails(response.data?.data);
    } catch {
      message.error("Failed to load journey log details");
      setSelectedLog(undefined);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (selectedLog) {
    return (
      <>
        <Space style={{ marginBottom: 16 }}>
          <Button
            onClick={() => {
              setSelectedLog(undefined);
              setDetails(undefined);
            }}
          >
            Back to journeys
          </Button>
          {journeyStatusTag(selectedLog.userJourney.status)}
        </Space>
        {loadingDetails ? (
          <Spin size="large" />
        ) : details ? (
          <>
            <Descriptions bordered size="small" column={{ xs: 1, md: 3 }}>
              <Descriptions.Item label="Journey">
                {journeyTitle(details.journey)}
              </Descriptions.Item>
              <Descriptions.Item label="Started">
                {formatDate(details.userJourney.startedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Completed">
                {formatDate(details.userJourney.completedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Progress">
                {journeyProgressLabel(details.userJourney)}
              </Descriptions.Item>
            </Descriptions>
            <h3 style={{ marginTop: 20 }}>Journey progress</h3>
            <JourneyProgressSteps
              journeySteps={details.journey.steps}
              stepProgress={details.userJourney.stepProgress}
              lessonAttempts={details.lessonAttempts}
              messages={details.messages}
              transcripts={details.userJourney.chatTranscripts}
            />
          </>
        ) : null}
      </>
    );
  }

  return (
    <Table<UserJourneyLogSummary>
      loading={loadingLogs}
      dataSource={logs}
      rowKey={(record) => record.userJourney._id}
      size="small"
      locale={{ emptyText: <Empty description="No journey progress" /> }}
      pagination={false}
      columns={[
        {
          title: "Journey",
          key: "journey",
          render: (_value, record) => journeyTitle(record.journey),
        },
        {
          title: "Started",
          dataIndex: ["userJourney", "startedAt"],
          render: formatDate,
          width: 180,
        },
        {
          title: "Status",
          dataIndex: ["userJourney", "status"],
          render: journeyStatusTag,
          width: 130,
        },
        {
          title: "Progress",
          key: "progress",
          render: (_value, record) => journeyProgressLabel(record.userJourney),
          width: 100,
        },
        {
          title: "Actions",
          key: "actions",
          width: 100,
          render: (_value, record) => (
            <Button
              type="link"
              size="small"
              onClick={() => openJourneyLog(record)}
            >
              Details
            </Button>
          ),
        },
      ]}
    />
  );
};

export default function UserEditPage() {
  const { state } = useLocation();
  const { id } = useParams<{ id: string }>();
  const isMounted = useRef(true);
  const [form] = Form.useForm();
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [premiumSaving, setPremiumSaving] = useState(false);
  const [testerSaving, setTesterSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<IUser | undefined>(state);
  const canEditUsers = hasPermission("users.edit");

  const navigate = useNavigate();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const applyUserToForm = useCallback(
    (user: IUser) => {
      setCurrentUser(user);
      form.setFieldsValue({
        ...user,
        isGenderMale: user.gender ?? user.isGenderMale,
      });
      if (user.avatar) {
        setSelectedAvatarUrl(
          user.avatar.startsWith("http")
            ? user.avatar
            : `/${user.avatar}_sm.jpg`,
        );
      }
    },
    [form],
  );

  useEffect(() => {
    const fetchUserData = async () => {
      if (!id) {
        navigate("/panel/users");
        return;
      }

      if (state && state._id === id) {
        applyUserToForm(state);
        setLoading(false);
      } else {
        try {
          setLoading(true);
          const response = await Api.get(
            `/api/admin/panel/users/profile/${id}`,
          );
          if (response.data?.data?.user) {
            applyUserToForm(response.data.data.user);
          } else {
            message.error("User not found");
            navigate("/panel/users");
          }
        } catch (error) {
          console.error("Error fetching user:", error);
          message.error("Failed to load user data");
          navigate("/panel/users");
        } finally {
          if (isMounted.current) setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [id, state, form, navigate, applyUserToForm]);

  const updatePremium = async (isPremiumUser: boolean) => {
    if (!currentUser?._id) return;
    setPremiumSaving(true);
    try {
      const response = await Api.put(
        `/api/admin/panel/users/${currentUser._id}/premium`,
        { isPremiumUser },
      );
      if (isMounted.current && response.data?.status === "successful") {
        const updatedUser = response.data?.data as IUser | undefined;
        const nextUser = {
          ...currentUser,
          ...updatedUser,
          isPremiumUser,
          premiumExpiresAt: null,
          premiumSource: "manual" as const,
          hasFreeSubscription: isPremiumUser,
        };
        setCurrentUser(nextUser);
        form.setFieldsValue(nextUser);
        message.success(
          isPremiumUser ? "User marked as premium" : "Premium removed",
        );
      } else if (isMounted.current) {
        message.error("Failed to update premium status");
      }
    } catch (error) {
      if (isMounted.current) {
        message.error("Failed to update premium status");
        console.error(error);
      }
    } finally {
      if (isMounted.current) setPremiumSaving(false);
    }
  };

  const updateTester = async (isTesterUser: boolean) => {
    if (!currentUser?._id) return;
    setTesterSaving(true);
    try {
      const response = await Api.put(
        `/api/admin/panel/users/${currentUser._id}/tester`,
        { isTesterUser },
      );
      if (isMounted.current && response.data?.status === "successful") {
        const updatedUser = response.data?.data as IUser | undefined;
        const nextUser = {
          ...currentUser,
          ...updatedUser,
          isTesterUser,
        };
        setCurrentUser(nextUser);
        form.setFieldValue("isTesterUser", isTesterUser);
        message.success(
          isTesterUser ? "User marked as tester" : "Tester access removed",
        );
      } else if (isMounted.current) {
        message.error("Failed to update tester status");
      }
    } catch (error) {
      if (isMounted.current) {
        message.error("Failed to update tester status");
        console.error(error);
      }
    } finally {
      if (isMounted.current) setTesterSaving(false);
    }
  };

  const updateUser = async (userData: IUser) => {
    setSaving(true);
    try {
      userData.avatar = currentUser?.avatar;
      const response = await Api.put(
        `/api/admin/panel/users/${userData._id}`,
        userData,
      );

      if (isMounted.current && response.status === 200) {
        message.success("User updated successfully");
        navigate("/panel/users");
      } else if (isMounted.current) {
        message.error("An error occurred while updating user");
      }
    } catch (error) {
      if (isMounted.current) {
        message.error("An error occurred");
        console.error(error);
      }
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px" }}>
          Loading user data...
        </div>
      </Card>
    );
  }

  if (!currentUser) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px" }}>
          User not found
        </div>
        <Button onClick={() => navigate("/panel/users")}>Back to Users</Button>
      </Card>
    );
  }

  return (
    <Card>
      <StickyFormActions
        onBack={() => navigate(-1)}
        actions={canEditUsers && (
          <Button
            loading={saving}
            type="primary"
            size="large"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
          >
            Update User
          </Button>
        )}
      />
      <Tabs
        defaultActiveKey="profile"
        items={[
          {
            key: "profile",
            label: "Profile",
            children: (
              <Form
                initialValues={currentUser}
                form={form}
                layout="vertical"
                onFinish={updateUser}
              >
                <Form.Item name="_id" hidden />

                <input
                  type="file"
                  hidden
                  id="avatar-selector"
                  disabled={!canEditUsers}
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      const file = e.target.files[0];
                      setSelectedAvatarUrl(URL.createObjectURL(file));
                      const formData = new FormData();
                      formData.append("file", file);
                      formData.append("filePath", `users/${+new Date()}`);
                      Api.post(`/api/files/photo`, formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                      }).then((res) => {
                        setCurrentUser({
                          ...currentUser,
                          avatar: res.data.data.md,
                        });
                        if (isMounted.current) {
                          message.success("Avatar uploaded successfully.");
                        }
                      });
                    }
                  }}
                />

                <div style={{ pointerEvents: canEditUsers ? undefined : "none" }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={16}>
                    <Card size="small" title="Profile">
                      <Row
                        gutter={16}
                        align="middle"
                        style={{ marginBottom: 16 }}
                      >
                        <Col flex="none">
                          <label
                            htmlFor="avatar-selector"
                            style={{ cursor: "pointer" }}
                          >
                            <Avatar
                              alt={currentUser.name || "User"}
                              size={72}
                              src={selectedAvatarUrl}
                            >
                              {!selectedAvatarUrl && <UserOutlined />}
                            </Avatar>
                          </label>
                        </Col>
                        <Col flex="auto">
                          <div style={{ fontWeight: 600, fontSize: 16 }}>
                            {currentUser.name || "—"}
                          </div>
                          <div
                            style={{
                              color: "rgba(0,0,0,.45)",
                              marginBottom: 6,
                            }}
                          >
                            {currentUser.email || "—"}
                          </div>
                          <Space size={[4, 4]} wrap>
                            {currentUser.isPremiumUser ? (
                              <Tag color="gold">Premium</Tag>
                            ) : (
                              <Tag>Free</Tag>
                            )}
                            {currentUser.isTesterUser && (
                              <Tag color="purple">Tester</Tag>
                            )}
                            {currentUser.isVerified && (
                              <Tag color="blue">Verified</Tag>
                            )}
                            {currentUser.isBanned && (
                              <Tag color="red">Banned</Tag>
                            )}
                            {currentUser.isTempUser && (
                              <Tag color="orange">Temp</Tag>
                            )}
                            {currentUser.isEmailVerified && (
                              <Tag color="green">Email verified</Tag>
                            )}
                          </Space>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col xs={24} sm={12}>
                          <Form.Item name="name" label="Name">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="email" label="Email">
                            <Input type="email" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="city" label="City">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="country" label="Country">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="isGenderMale" label="Gender">
                            <Select allowClear placeholder="Select">
                              <Select.Option value="male">Male</Select.Option>
                              <Select.Option value="female">
                                Female
                              </Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="originLanguage"
                            label="Origin language"
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="targetLanguage"
                            label="Target language"
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="languageLevel"
                            label="Language level"
                          >
                            <Input placeholder="e.g. a2" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="availableMessagesCount"
                            label="Available messages"
                          >
                            <InputNumber min={0} style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="bio" label="Bio">
                            <Input.TextArea rows={2} />
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item
                            name="goals"
                            label="Goals"
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea rows={2} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Card
                      size="small"
                      title="Premium"
                      extra={
                        <Switch
                          checked={!!currentUser.isPremiumUser}
                          disabled={!canEditUsers}
                          loading={premiumSaving}
                          onChange={updatePremium}
                          checkedChildren="On"
                          unCheckedChildren="Off"
                        />
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Source">
                          {currentUser.premiumSource || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Expires">
                          {formatDate(currentUser.premiumExpiresAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Free sub">
                          {formatBool(currentUser.hasFreeSubscription)}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card
                      size="small"
                      title="Tester user"
                      extra={
                        <Switch
                          checked={!!currentUser.isTesterUser}
                          disabled={!canEditUsers}
                          loading={testerSaving}
                          onChange={updateTester}
                          checkedChildren="On"
                          unCheckedChildren="Off"
                        />
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <span style={{ color: "rgba(0,0,0,.45)", fontSize: 12 }}>
                        Can see unpublished content hidden from other users.
                      </span>
                    </Card>

                    <Card size="small" title="App activity">
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Registered">
                          {formatDate(currentUser.registerDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Last run">
                          {formatDate(currentUser.lastRunTime)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Last activity">
                          {formatDate(currentUser.lastActivityDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Version">
                          {currentUser.installedVersion || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Installed from">
                          {currentUser.installedFrom || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Run times">
                          {currentUser.runTimes ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="FCM expired">
                          {formatBool(currentUser.fcmExpired)}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col xs={24} md={12}>
                    <Card size="small" title="Progress">
                      <Descriptions size="small" column={2}>
                        <Descriptions.Item label="XP">
                          {currentUser.xp ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Level">
                          {currentUser.progressionLevel ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Missions">
                          {currentUser.totalMissionsCompleted ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="First mission">
                          {formatBool(currentUser.isFirstMissionPassed)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Streak">
                          {currentUser.currentStreak ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Best streak">
                          {currentUser.bestStreak ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Daily commitment">
                          {currentUser.dailyCommitmentMinutes != null
                            ? `${currentUser.dailyCommitmentMinutes} min`
                            : "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Level verified">
                          {formatBool(currentUser.isLevelVerified)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Learning reasons" span={2}>
                          {currentUser.learningReasons?.length
                            ? currentUser.learningReasons.join(", ")
                            : "—"}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col xs={24} md={12}>
                    <Card size="small" title="Social & usage">
                      <Descriptions size="small" column={2}>
                        <Descriptions.Item label="Followers">
                          {currentUser.followersCount ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Following">
                          {currentUser.followingCount ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Friends">
                          {currentUser.friendsCount ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Verified">
                          {formatBool(currentUser.isVerified)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Daily msgs">
                          {currentUser.dailyUsedMessagesCount ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Total msgs">
                          {currentUser.totalMessagesCount ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Partner finds">
                          {currentUser.dailyPartnerFindCount ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Used msgs">
                          {currentUser.usageCount ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Referral">
                          {currentUser.referralCode ?? "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="From referral">
                          {currentUser.installedFromReferral ?? "—"}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col span={24}>
                    <Card size="small" title="Account details">
                      <Descriptions
                        size="small"
                        column={{ xs: 1, sm: 2, md: 3 }}
                      >
                        <Descriptions.Item label="Job title">
                          {currentUser.jobTitle || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Country code">
                          {currentUser.countryCode || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Gender">
                          {currentUser.gender || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Banned">
                          {formatBool(currentUser.isBanned)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ban reason">
                          {currentUser.banReason || "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ban expires">
                          {formatDate(currentUser.banExpireDate)}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                </Row>
                </div>
              </Form>
            ),
          },
          {
            key: "logs",
            label: "Logs",
            children: id ? (
              <UserLogTimeline userId={id} />
            ) : (
              <div style={{ padding: 24 }}>No user selected.</div>
            ),
          },
          {
            key: "journey-progress",
            label: "Journey progress",
            children: id ? (
              <UserJourneyProgressTab userId={id} />
            ) : (
              <div style={{ padding: 24 }}>No user selected.</div>
            ),
          },
        ]}
      />
    </Card>
  );
}
